import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, FileText, HelpCircle, Award, Eye, Edit, Trash2, Clock, X } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { useDialog } from '../components/DialogProvider';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-hot-toast';

const TestsManagement = () => {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const { getHomeContents, getTestQuestions, createTestMetadata, deleteTestQuestions, clearTestCache } = useFirebase();
  const dialog = useDialog();
  const hasShownExpiredNotification = useRef(false);

  const [contentDetails, setContentDetails] = useState(null);
  const [tests, setTests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Format a value (Date | Firestore Timestamp | string) as DD-MM-YYYY
  const formatDateDMY = (value) => {
    if (!value) return 'N/A'
    try {
      let date
      if (value instanceof Date) {
        date = value
      } else if (value.toDate && typeof value.toDate === 'function') {
        date = value.toDate()
      } else {
        date = new Date(value)
      }
      if (isNaN(date.getTime())) return 'Invalid Date'
      const dd = String(date.getDate()).padStart(2, '0')
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const yyyy = date.getFullYear()
      return `${dd}-${mm}-${yyyy}`
    } catch {
      return 'Invalid Date'
    }
  }

  // Form modal state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    testName: '',
    startDate: '',
    endDate: '',
    duration: '', // Add duration field (in minutes)
    marks: '' // Add marks field for manual input
  });

  // Helper function to format dates for URL parameters
  const formatDateForUrl = (dateValue) => {
    if (!dateValue) return ''
    try {
      let date
      if (dateValue instanceof Date) {
        date = dateValue
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate()
      } else {
        date = new Date(dateValue)
      }

      if (isNaN(date.getTime())) return ''

      // Format as YYYY-MM-DD for HTML date input
      return date.toISOString().split('T')[0]
    } catch (error) {
      console.error('Error formatting date for URL:', error)
      return ''
    }
  }

  useEffect(() => {
    if (contentId) {
      loadContentAndTests();
    }
  }, [contentId]);

  // Check for expired tests and auto-delete them
  const checkAndDeleteExpiredTests = async () => {
    try {
      const currentDate = new Date();
      const expiredTests = tests.filter(test => {
        if (!test.endDate) return false;

        let endDate;
        if (test.endDate.toDate && typeof test.endDate.toDate === 'function') {
          endDate = test.endDate.toDate();
        } else {
          endDate = new Date(test.endDate);
        }

        return endDate < currentDate;
      });

      if (expiredTests.length > 0) {
        console.log(`🗑️ Found ${expiredTests.length} expired tests to delete`);

        for (const expiredTest of expiredTests) {
          try {
            await deleteTestQuestions(contentId, expiredTest.id);
            console.log(`✅ Auto-deleted expired test: ${expiredTest.testName || expiredTest.title}`);
          } catch (error) {
            console.error(`❌ Failed to auto-delete test ${expiredTest.testName}:`, error);
          }
        }

        // Reload tests after deletion and show notification only once
        if (expiredTests.length > 0) {
          if (!hasShownExpiredNotification.current) {
            toast.success(`Automatically removed ${expiredTests.length} expired test(s)`);
            hasShownExpiredNotification.current = true;
          }
          await loadContentAndTests();
        }
      }
    } catch (error) {
      console.error('Error checking for expired tests:', error);
    }
  };

  const loadContentAndTests = async () => {
    try {
      setLoading(true);

      // Load content details
      const contents = await getHomeContents();
      const content = contents.find(c => c.id === contentId);
      setContentDetails(content);

      // Load tests for this content (force refresh to get latest data)
      const testQuestions = await getTestQuestions(contentId, true);

      if (testQuestions && testQuestions.length > 0) {
        // The testQuestions array already contains complete test objects with marks
        // No need to group by title, just use them directly
        setTests(testQuestions);

        // After loading tests, check for expired ones
        setTimeout(() => checkAndDeleteExpiredTests(), 1000);

      } else {
        setTests([]);
      }
    } catch (error) {
      console.error('Error loading content and tests:', error);
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTests = tests.filter(test =>
    (test.testName || test.title || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateNewTest = () => {
    setShowCreateForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.testName.trim() || !formData.startDate || !formData.endDate || !formData.marks) {
      dialog.alert({ title: 'Missing Fields', message: 'Please fill in all fields including marks.' });
      return;
    }

    try {
      setLoading(true);

      // Prepare navigation params BEFORE creating the test
      const params = new URLSearchParams()
      params.set('testName', formData.testName)
      if (formData.startDate) params.set('startDate', formData.startDate)
      if (formData.endDate) params.set('endDate', formData.endDate)
      if (formData.duration) params.set('duration', formData.duration)
      if (formData.marks) params.set('marks', formData.marks)

      // Close the form and reset IMMEDIATELY (before async operations)
      handleCloseForm();

      // Navigate to add questions - the test creation will happen in AddTest.jsx's addTestQuestions
      // which properly handles the existing test check
      toast.success('Navigating to add questions...')
      navigate(`/add-test/${contentId}?${params.toString()}`);
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error(`Error: ${error.message}`)
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setFormData({ testName: '', startDate: '', endDate: '', duration: '', marks: '' });
  };

  const handleEditTest = async (test) => {
    const ok = await dialog.confirm({
      title: 'Edit Test',
      message: `Do you want to edit "${test.testName || test.title}"?`,
      confirmText: 'Yes, Edit',
      tone: 'primary'
    })
    if (!ok) return
    const params = new URLSearchParams()
    params.set('edit', test.testName || test.title)
    if (test.startDate) params.set('startDate', formatDateForUrl(test.startDate))
    if (test.endDate) params.set('endDate', formatDateForUrl(test.endDate))
    if (test.duration) params.set('duration', test.duration.toString())
    if (test.marks !== undefined && test.marks !== null) params.set('marks', test.marks.toString())
    toast.success('Proceeding to edit test...')
    navigate(`/add-test/${contentId}?${params.toString()}`)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteTest = async (test) => {
    const confirmMessage = `Are you sure you want to delete "${test.testName || test.title}"? This will permanently delete the test and all associated questions.`;
    const ok = await dialog.confirm({ title: 'Delete Test', message: confirmMessage, confirmText: 'Yes, Delete', tone: 'danger' });
    if (ok) {
      try {
        setLoading(true);
        console.log('🗑️ Deleting test:', test);

        // Delete the test from Firestore and clear cache
        await deleteTestQuestions(contentId, test.id);

        // Force reload the tests to reflect the deletion
        await loadContentAndTests();
        toast.success(`Test "${test.testName || test.title}" deleted successfully.`)
      } catch (error) {
        console.error('❌ Error deleting test:', error);
        toast.error(`Failed to delete test: ${error.message}`)
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Purple Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/home')}
                className="p-2 hover:bg-purple-500 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-8 h-8" />
                  <h1 className="text-2xl font-bold">Tests Management</h1>
                </div>
                <p className="text-purple-100">
                  Manage tests for: <span className="font-medium">{contentDetails?.title || 'Loading...'}</span>
                </p>
              </div>
            </div>
            <button
              onClick={handleCreateNewTest}
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-purple-50 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create New Test
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">

        {/* Search Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tests by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Tests List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              Tests ({filteredTests.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-10 flex items-center justify-center text-gray-600">
              <LoadingSpinner size="large" />
              <span className="ml-3">Loading tests...</span>
            </div>
          ) : filteredTests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Test Name</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Questions</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Start Date</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">End Date</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Duration</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Marks</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">View Count</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Add Questions</th>
                    <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTests.map((test) => (
                    <tr key={test.id} className="border-b border-gray-100 hover:bg-gray-50">
                      {/* Test Name */}
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <FileText className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{test.testName || test.title}</div>
                            <div className="text-sm text-gray-500">
                              Created: {formatDateDMY(test.createdAt)}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Questions Count */}
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center justify-center space-x-1">
                          <HelpCircle className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-blue-600">{test.questions.length}</span>
                        </div>
                      </td>

                      {/* Start Date */}
                      <td className="py-4 px-6 text-left">
                        <div className="text-sm text-gray-600">
                          {test.startDate ? formatDateDMY(test.startDate) : formatDateDMY(new Date())}
                        </div>
                      </td>

                      {/* End Date */}
                      <td className="py-4 px-6 text-left">
                        <div className="text-sm text-gray-600">
                          {test.endDate ? formatDateDMY(test.endDate) :
                            formatDateDMY(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))}
                        </div>
                      </td>

                      {/* Duration */}
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center justify-center space-x-1">
                          <Clock className="w-4 h-4 text-purple-500" />
                          <span className="font-medium text-purple-600">
                            {test.duration ? `${test.duration} min` : '60 min'}
                          </span>
                        </div>
                      </td>

                      {/* Marks */}
                      <td className="py-4 px-6 text-left">
                        <span className="font-medium text-green-600">{test.marks || 0}</span>
                      </td>

                      {/* View Count */}
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center justify-center space-x-1">
                          <Eye className="w-4 h-4 text-orange-500" />
                          <span className="font-medium text-orange-600">{test.viewCount || 0}</span>
                        </div>
                      </td>

                      {/* Add Questions */}
                      <td className="py-4 px-6 text-left">
                        <button
                          onClick={() => {
                            const params = new URLSearchParams()
                            params.set('edit', test.testName || test.title)
                            if (test.startDate) params.set('startDate', formatDateForUrl(test.startDate))
                            if (test.endDate) params.set('endDate', formatDateForUrl(test.endDate))
                            if (test.duration) params.set('duration', test.duration.toString())
                            if (test.marks !== undefined && test.marks !== null) params.set('marks', test.marks.toString())
                            navigate(`/add-test/${contentId}?${params.toString()}`)
                          }}
                          className="p-2 bg-purple-100 text-purple-600 rounded-full hover:bg-purple-200 transition-colors"
                          title="Add More Questions"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => navigate(`/test-questions/${test.id}?contentId=${contentId}&testName=${encodeURIComponent(test.testName || test.title)}`)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="View Questions"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditTest(test)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Edit Test"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTest(test)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete Test"
                            disabled={loading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tests found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? `No tests match "${searchTerm}". Try a different search term.`
                  : 'Get started by creating your first test'
                }
              </p>
              <button
                onClick={handleCreateNewTest}
                className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create First Test
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Test Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-900">Create New Test</h2>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar">

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label htmlFor="testName" className="block text-sm font-medium text-gray-700 mb-1">
                    Test Name *
                  </label>
                  <input
                    type="text"
                    id="testName"
                    name="testName"
                    value={formData.testName}
                    onChange={handleInputChange}
                    placeholder="Enter test name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="e.g., 60"
                    min="1"
                    max="600"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="marks" className="block text-sm font-medium text-gray-700 mb-1">
                    Total Marks *
                  </label>
                  <input
                    type="number"
                    id="marks"
                    name="marks"
                    value={formData.marks}
                    onChange={handleInputChange}
                    placeholder="e.g., 30"
                    min="1"
                    max="1000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Test'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestsManagement;